import { ApiRequestError, apiErrorCode, type ApiClient, type ApiErrorCode, type Appointment, type ClockPhoto, type ClockPhotoRequest } from "@shyne/shared/api";
import type { uuid } from "@/interfaces";

type FieldActionsApi = Pick<ApiClient["appointments"], "accept" | "start" | "beginTasks" | "finish" | "handBack" | "addExtraPhoto" | "removeExtraPhoto" | "setClockPhoto">;

export const BLANK_HAND_BACK_REASON = "Requested reassignment from appointment list";

const CHECKLIST_INCOMPLETE_MESSAGE = "All service tasks must be completed before finishing work";
const SESSION_EXPIRED_MESSAGE = "Your session has expired. Please sign in again.";
const NOT_ALLOWED_MESSAGE = "You don't have permission to do that.";
const OFFLINE_MESSAGE = "Can't reach the server. Check your connection and try again.";

export type ApiRefusal = { code: ApiErrorCode; message: string };

export function apiRefusalOf(error: unknown): ApiRefusal | null {
  if (error instanceof ApiRequestError) return { code: error.code, message: error.message };
  if (typeof error !== "object" || error === null) return null;
  const { code, message } = error as { code?: unknown; message?: unknown };
  const known = apiErrorCode.safeParse(code);
  return known.success && typeof message === "string" ? { code: known.data, message } : null;
}

export function holdMessageOr(error: unknown, fallback: string): string {
  const refusal = apiRefusalOf(error);
  return refusal?.code === "writes_held" ? refusal.message : fallback;
}

const CONFLICT_CODES: ReadonlySet<ApiErrorCode> = new Set(["wrong_status", "checklist_incomplete", "not_assigned", "cancellation_cutoff", "go_back_window_closed", "invoiced"]);

const REASON_SHOWN_CODES: ReadonlySet<ApiErrorCode> = new Set([...CONFLICT_CODES, "invalid", "no_price", "reason_required", "writes_held"]);

const refusedWith = (error: unknown, code: ApiErrorCode) => apiRefusalOf(error)?.code === code;

export const isIncompleteChecklist = (error: unknown) => refusedWith(error, "checklist_incomplete");

export const isWrongStatus = (error: unknown) => refusedWith(error, "wrong_status");

export const isReasonRequired = (error: unknown) => refusedWith(error, "reason_required");

export const isConflictRefusal = (error: unknown) => {
  const refusal = apiRefusalOf(error);
  return refusal !== null && CONFLICT_CODES.has(refusal.code);
};

const EVIDENCE_GONE_CODES: ReadonlySet<ApiErrorCode> = new Set(["forbidden", "not_found"]);

export const rereadsAfterEvidenceRefusal = (error: unknown) => {
  const refusal = apiRefusalOf(error);
  return refusal !== null && (CONFLICT_CODES.has(refusal.code) || EVIDENCE_GONE_CODES.has(refusal.code));
};

const isUnreachable = (error: unknown) => error instanceof TypeError && /fetch|network|load failed/i.test(error.message);

export const clockPhoto = (photoUrl: string, position: GeolocationPosition | null): ClockPhoto =>
  position ? { photoUrl, latitude: position.coords.latitude, longitude: position.coords.longitude } : { photoUrl };

export type TakenPhoto = { photo_captured_at?: string; lat?: number; lng?: number };

export const vendorClockPhoto = (type: "clockin" | "clockout", photoUrl: string, { photo_captured_at, lat, lng }: TakenPhoto): ClockPhotoRequest => ({
  clock: type === "clockin" ? "in" : "out",
  photoUrl,
  ...(typeof lat === "number" ? { latitude: lat } : {}),
  ...(typeof lng === "number" ? { longitude: lng } : {}),
  ...(photo_captured_at ? { takenAt: photo_captured_at } : {}),
});

export function fieldActionMessage(error: unknown, fallback: string): string {
  if (isUnreachable(error)) return OFFLINE_MESSAGE;
  const refusal = apiRefusalOf(error);
  if (refusal === null) return fallback;
  if (refusal.code === "checklist_incomplete") return CHECKLIST_INCOMPLETE_MESSAGE;
  if (REASON_SHOWN_CODES.has(refusal.code)) return refusal.message;
  if (refusal.code === "unauthenticated") return SESSION_EXPIRED_MESSAGE;
  if (refusal.code === "forbidden") return NOT_ALLOWED_MESSAGE;
  return fallback;
}

export function photoRefusalMessage(error: unknown, fallback: string): string {
  const refusal = apiRefusalOf(error);
  if (refusal?.code === "checklist_incomplete") return refusal.message;
  const ownMessage = refusal === null && error instanceof Error && error.message ? error.message : fallback;
  return fieldActionMessage(error, ownMessage);
}

export function createFieldActions(api: FieldActionsApi) {
  return {
    accept(id: uuid): Promise<Appointment> {
      return api.accept(id);
    },

    start(id: uuid, clock: ClockPhoto): Promise<Appointment> {
      return api.start(id, clock);
    },

    beginTasks(id: uuid): Promise<Appointment> {
      return api.beginTasks(id);
    },

    finish(id: uuid, clock: ClockPhoto): Promise<Appointment> {
      return api.finish(id, clock);
    },

    addExtraPhoto(id: uuid, photoUrl: string, isAfter: boolean): Promise<Appointment> {
      return api.addExtraPhoto(id, { photoUrl, isAfter });
    },

    removeExtraPhoto(id: uuid, photoUrl: string): Promise<Appointment> {
      return api.removeExtraPhoto(id, { photoUrl });
    },

    recordClockPhoto(id: uuid, request: ClockPhotoRequest): Promise<Appointment> {
      return api.setClockPhoto(id, request);
    },

    handBack(id: uuid, reason: string, actingOrganization: uuid | null): Promise<Appointment | null> {
      return api.handBack(id, {
        reason: reason.trim() || BLANK_HAND_BACK_REASON,
        ...(actingOrganization ? { actingOrganization } : {}),
      });
    },
  };
}
